import React, { useState, useEffect, useRef } from 'react';
import {
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Divider,
  Backdrop,
  CircularProgress,
  Collapse,
  IconButton,
  Tabs,
  Tab,
  Autocomplete,
  TextField,
  Button
} from '@mui/material';
import { ExpandMore, ExpandLess, DragIndicator, Feedback as FeedbackIcon, Download as DownloadIcon } from '@mui/icons-material';
import FeedbackModal from './FeedbackModal';
import { apiServices } from '../services';
import { UNKNOWN_STATUS, matchesRefStatusFilter, matchesFlowStatusFilter } from '../services/helperServices';

const StationSelectorModal = ({ 
  counties,
  regions,
  countyStations,
  onStationSelect,
  selectedCounty,
  setSelectedCounty,
  selectedRegion,
  setSelectedRegion,
  selectedStationId,
  setSelectedStationId,
  fetchStationInfo,
  stationInfoLoading,
  stationsLoading,
  selectedStationData,
  onTabChange,
  refStatusFilter,
  setRefStatusFilter,
  flowStatusFilter,
  setFlowStatusFilter
}) => {
  const [loading, setLoading] = useState(false);
  const [selectedMasterId, setSelectedMasterId] = useState('');
  const [isMinimized, setIsMinimized] = useState(false);
  const [width, setWidth] = useState(320);
  const [isResizing, setIsResizing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 20, y: 80 });
  const [activeTab, setActiveTab] = useState(0); // 0 for Region, 1 for County
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const containerRef = useRef(null);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);
  const dragStartRef = useRef({ x: 0, y: 0 });

  // Counties are now passed directly as a prop
  // Stations for the selected county are also passed as countyStations prop

  // Reset station selection and filters when county or region changes
  useEffect(() => {
    setSelectedStationId('');
    setSelectedMasterId('');
    setRefStatusFilter('');
    setFlowStatusFilter('');
  }, [selectedCounty, selectedRegion]);

  // Filter stations by ref status and flow status
  // Use refstatus_values/flowstatus_values (comma-separated distinct values per masterid)
  // so a masterid is included if ANY of its stationid_aliases match the filter
  const filteredStations = countyStations.filter(station =>
    matchesRefStatusFilter(station.refstatus_values || station.refstatus_final, refStatusFilter) &&
    matchesFlowStatusFilter(station.flowstatus_values || station.flowstatus_final, flowStatusFilter)
  );

  // Update masterid when selectedStationId changes (e.g., from map click)
  useEffect(() => {
    if (selectedStationId && countyStations) {
      const station = countyStations.find(s => s.stationid === selectedStationId);
      if (station && station.masterid) {
        setSelectedMasterId(station.masterid);
      }
    }
  }, [selectedStationId, countyStations]);

  // Set active tab based on what's selected (for initial load)
  useEffect(() => {
    if (selectedRegion && !selectedCounty) {
      setActiveTab(0);
    } else if (selectedCounty && !selectedRegion) {
      setActiveTab(1);
    }
  }, []);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    // Clear selections and auto-select first item when switching tabs
    if (newValue === 0) {
      // Switching to Region tab
      setSelectedCounty('');
      // Auto-select first region if available
      if (regions && regions.length > 0) {
        setSelectedRegion(regions[0]);
      }
    } else {
      // Switching to County tab
      setSelectedRegion('');
      // Auto-select first county if available
      if (counties && counties.length > 0) {
        setSelectedCounty(counties[0]);
      }
    }
    
    // Trigger zoom to California when tab changes
    if (onTabChange) {
      onTabChange();
    }
  };

  const handleCountyChange = (event) => {
    setSelectedCounty(event.target.value);
    setSelectedRegion(''); // Clear region when county is selected
  };

  const handleRegionChange = (event) => {
    setSelectedRegion(event.target.value);
    setSelectedCounty(''); // Clear county when region is selected
  };

  const handleStationChange = async (event) => {
    const stationId = event.target.value;
    setSelectedStationId(stationId);
    
    // Find the station's masterid from countyStations
    if (stationId && countyStations) {
      const station = countyStations.find(s => s.stationid === stationId);
      if (station && station.masterid) {
        setSelectedMasterId(station.masterid);
        
        // Fetch station info once and store it
        setLoading(true);
        const detailedInfo = await fetchStationInfo(station.masterid);
        setLoading(false);
        
        if (detailedInfo) {
          // The parent component will store this in selectedStationData
          // Also trigger map update with geometry (site_geometry will be added from allStations)
          onStationSelect({
            stationid: stationId,
            masterid: station.masterid,
            county: selectedCounty,
            catchment_geometry: detailedInfo.catchment_geometry
          });
        }
      }
    }
  };

  // Handle download all data
  const handleDownloadAllData = async () => {
    setIsDownloading(true);
    try {
      const response = await apiServices.downloadAllData();
      if (response.success && response.content) {
        // Decode base64 Excel content
        const byteCharacters = atob(response.content);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `refscreens_data_${Date.now()}.xlsx`;
        a.click();
        URL.revokeObjectURL(url);
      } else {
        alert('Failed to download data: ' + (response.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error downloading data:', error);
      alert('Error downloading data: ' + error.message);
    } finally {
      setIsDownloading(false);
    }
  };


  // Handle resize
  const handleResizeMouseDown = (e) => {
    setIsResizing(true);
    startXRef.current = e.clientX;
    startWidthRef.current = width;
    e.preventDefault();
    e.stopPropagation();
  };

  // Handle drag
  const handleDragMouseDown = (e) => {
    if (e.target.closest('.resize-handle') || e.target.closest('button')) return;
    setIsDragging(true);
    dragStartRef.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y
    };
    e.preventDefault();
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isResizing) {
        const deltaX = e.clientX - startXRef.current;
        const newWidth = Math.max(250, Math.min(600, startWidthRef.current + deltaX));
        setWidth(newWidth);
      } else if (isDragging) {
        setPosition({
          x: e.clientX - dragStartRef.current.x,
          y: e.clientY - dragStartRef.current.y
        });
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      setIsDragging(false);
    };

    if (isResizing || isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isResizing, isDragging, position]);


  return (
    <>
    <Paper 
      ref={containerRef}
      elevation={6}
      sx={{ 
        position: 'absolute',
        left: position.x,
        top: position.y,
        width: isMinimized ? 200 : width,
        height: 'fit-content',
        maxHeight: 'calc(100vh - 100px)',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 1,
        border: 1,
        borderColor: 'divider',
        transition: isResizing || isDragging ? 'none' : 'width 0.3s ease',
        overflow: 'hidden',
        zIndex: 1000,
        cursor: isDragging ? 'grabbing' : 'default',
        userSelect: isDragging ? 'none' : 'auto'
      }}
    >
      {/* Resize Handle */}
      {!isMinimized && (
        <Box
          className="resize-handle"
          onMouseDown={handleResizeMouseDown}
          sx={{
            position: 'absolute',
            right: 0,
            top: 0,
            bottom: 0,
            width: 8,
            cursor: 'ew-resize',
            bgcolor: 'transparent',
            '&:hover': {
              bgcolor: 'primary.light',
              opacity: 0.3
            },
            zIndex: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <DragIndicator 
            sx={{ 
              fontSize: 16, 
              color: 'text.secondary',
              opacity: 0.5,
              pointerEvents: 'none'
            }} 
          />
        </Box>
      )}
      
      <Box 
        onMouseDown={handleDragMouseDown}
        sx={{ 
          p: isMinimized ? 1 : 2, 
          bgcolor: 'primary.main', 
          color: 'primary.contrastText',
          display: 'flex',
          alignItems: 'center',
          justifyContent: isMinimized ? 'center' : 'space-between',
          cursor: isDragging ? 'grabbing' : 'grab'
        }}
      >
        {!isMinimized && (
          <Typography variant="h6">
            Station Selector
          </Typography>
        )}
        <IconButton 
          size="small" 
          sx={{ color: 'inherit' }}
          onClick={() => setIsMinimized(!isMinimized)}
        >
          {isMinimized ? <ExpandMore /> : <ExpandLess />}
        </IconButton>
      </Box>
      
      <Collapse in={!isMinimized} timeout="auto">
        {stationsLoading ? (
          <Box 
            sx={{ 
              p: 4,
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              minHeight: 200
            }}
          >
            <CircularProgress size={60} />
            <Typography variant="h6" sx={{ mt: 2 }}>
              Loading stations...
            </Typography>
          </Box>
        ) : (
          <Box sx={{ p: 2, overflowY: 'auto', maxHeight: 'calc(100vh - 200px)' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Download All Data Button */}
          <Button
            variant="outlined"
            color="primary"
            startIcon={isDownloading ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
            onClick={handleDownloadAllData}
            disabled={isDownloading}
            fullWidth
            size="small"
          >
            {isDownloading ? 'Downloading...' : 'Download All Data'}
          </Button>

          {/* Reset Filters Button */}
          <Button
            variant="outlined"
            color="primary"
            onClick={() => {
              setRefStatusFilter('');
              setFlowStatusFilter('');
              setSelectedStationId('');
              setSelectedMasterId('');
              if (activeTab === 0) {
                setSelectedCounty('');
                setSelectedRegion(regions && regions.length > 0 ? regions[0] : '');
              } else {
                setSelectedRegion('');
                setSelectedCounty(counties && counties.length > 0 ? counties[0] : '');
              }
              if (onTabChange) onTabChange();
            }}
            fullWidth
            size="small"
          >
            Reset App
          </Button>

          {/* Tabs for Region/County Selection */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={activeTab} onChange={handleTabChange} variant="fullWidth">
              <Tab 
                label="By Region" 
                iconPosition="start"
                sx={{ minHeight: 48 }}
              />
              <Tab 
                label="By County" 
                iconPosition="start"
                sx={{ minHeight: 48 }}
              />
            </Tabs>
          </Box>

          {/* Region Selection Panel */}
          {activeTab === 0 && (
            <FormControl fullWidth size="small">
              <InputLabel id="region-select-label">Select a region</InputLabel>
              <Select
                labelId="region-select-label"
                id="region-select"
                value={selectedRegion}
                label="Select a region"
                onChange={handleRegionChange}
                disabled={!regions || regions.length === 0}
              >
                {regions && regions.map((region) => (
                  <MenuItem key={region} value={region}>
                    {region}
                  </MenuItem>
                ))}
              </Select>
              {(!regions || regions.length === 0) && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                  No regions available
                </Typography>
              )}
            </FormControl>
          )}

          {/* County Selection Panel */}
          {activeTab === 1 && (
            <FormControl fullWidth size="small">
              <InputLabel id="county-select-label">Select a county</InputLabel>
              <Select
                labelId="county-select-label"
                id="county-select"
                value={selectedCounty}
                label="Select a county"
                onChange={handleCountyChange}
                disabled={!counties || counties.length === 0}
              >
                {counties && counties.map((county) => (
                  <MenuItem key={county} value={county}>
                    {county}
                  </MenuItem>
                ))}
              </Select>
              {(!counties || counties.length === 0) && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                  No counties available
                </Typography>
              )}
            </FormControl>
          )}

          {/* Status Filters - shown when a region or county is selected */}
          {(selectedCounty || selectedRegion) && countyStations.length > 0 && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <FormControl size="small" sx={{ flex: 1 }}>
                <InputLabel id="ref-status-filter-label">Ref Status</InputLabel>
                <Select
                  labelId="ref-status-filter-label"
                  value={refStatusFilter}
                  label="Ref Status"
                  onChange={(e) => setRefStatusFilter(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="Reference">Reference</MenuItem>
                  <MenuItem value="Intermediate">Intermediate</MenuItem>
                  <MenuItem value="Stressed">Stressed</MenuItem>
                  <MenuItem value={UNKNOWN_STATUS}>Unknown</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ flex: 1 }}>
                <InputLabel id="flow-status-filter-label">Flow Status</InputLabel>
                <Select
                  labelId="flow-status-filter-label"
                  value={flowStatusFilter}
                  label="Flow Status"
                  onChange={(e) => setFlowStatusFilter(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="P">Perennial</MenuItem>
                  <MenuItem value="NP">Non-Perennial</MenuItem>
                  <MenuItem value="I">Intermittent</MenuItem>
                  <MenuItem value="RFI">RFI</MenuItem>
                  <MenuItem value="SFI">SFI</MenuItem>
                  <MenuItem value="E">Ephemeral</MenuItem>
                  <MenuItem value={UNKNOWN_STATUS}>Unknown</MenuItem>
                </Select>
              </FormControl>
            </Box>
          )}

          <Divider />

          {/* Station ID Selection */}
          <Autocomplete
            size="small"
            fullWidth
            disabled={(!selectedCounty && !selectedRegion)}
            options={[...new Set(filteredStations.map((station) => station.stationid))].sort()}
            value={selectedStationId || null}
            onChange={(event, newValue) => {
              if (newValue) {
                handleStationChange({ target: { value: newValue } });
              } else {
                // Handle clearing the selection
                setSelectedStationId('');
                setSelectedMasterId('');
              }
            }}
            filterOptions={(options, { inputValue }) => {
              if (!inputValue) return options;
              // Only show exact matches or options that start with the input
              return options.filter(option => 
                option === inputValue || option.startsWith(inputValue.toUpperCase())
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Station ID"
                placeholder="Type or select a station"
                helperText={
                  !selectedCounty && !selectedRegion
                    ? "Please select a region or county first"
                    : filteredStations.length === 0
                    ? "No stations match the selected filters"
                    : `${new Set(filteredStations.map(s => s.masterid)).size} sites — type to search or select`
                }
              />
            )}
            freeSolo={false}
            clearOnEscape
            autoHighlight
          />

          {/* Selected Station Info */}
          {selectedStationId && (
            <>
              <Divider />
              <Box sx={{ 
                p: 2, 
                bgcolor: 'background.default', 
                borderRadius: 1,
                border: 1,
                borderColor: 'primary.main'
              }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Master ID:
                </Typography>
                <Typography variant="h6" color="primary">
                  {selectedMasterId || 'N/A'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Station ID Aliases: {selectedStationData?.stationid_aliases || selectedStationId || 'N/A'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Station Name Aliases: {selectedStationData?.stationname_aliases || 'N/A'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {selectedCounty ? `${selectedCounty} County` : selectedRegion ? `${selectedRegion} Region` : 'N/A'}
                </Typography>
              </Box>
              
              {/* Provide Feedback Button */}
              <Button
                variant="contained"
                color="info"
                startIcon={<FeedbackIcon />}
                onClick={() => setFeedbackModalOpen(true)}
                fullWidth
                sx={{ mt: 1 }}
              >
                Provide Feedback
              </Button>
            </>
          )}
          </Box>
          </Box>
        )}
      </Collapse>
    </Paper>
    
    {/* Feedback Modal */}
    <FeedbackModal
      open={feedbackModalOpen}
      onClose={() => setFeedbackModalOpen(false)}
      masterid={selectedMasterId}
      refstatusFinal={selectedStationData?.refstatus_final}
      flowstatusFinal={selectedStationData?.flowstatus_final}
    />
    <Backdrop
      sx={{ color: '#fff', zIndex: 9999, flexDirection: 'column', gap: 2 }}
      open={isDownloading}
    >
      <CircularProgress color="inherit" />
      <Typography variant="h6">Downloading data, please wait...This might take a few moments.</Typography>
    </Backdrop>
    </>
  );
};

export default StationSelectorModal;